import { UnitSystem } from "@/types/geo"

export function weatherIconSrc(icon?: string) {
  return icon && /^\d{2}[dn]$/.test(icon)
    ? `/img/openweathermap/${icon}.svg`
    : undefined
}

export const formatWeatherDay = (
  locale: string,
  timestamp: number,
  timeZone: string
) => {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone,
  }).format(timestamp * 1000)
}

export const formatWeatherTemp = (
  locale: string,
  temp: string | undefined,
  unitSystem: UnitSystem
) => {
  if (!temp) return temp

  const match = temp.trim().match(/^(-?\d+(?:\.\d+)?)\s*\u00b0?\s*([FC])?$/i)
  if (!match) {
    return temp.replace(" F", "\u00b0F").replace(" C", "\u00b0C")
  }

  const value = Number(match[1])
  if (!Number.isFinite(value)) {
    return temp.replace(" F", "\u00b0F").replace(" C", "\u00b0C")
  }

  const sourceUnit = match[2]?.toUpperCase() || "F"

  if (unitSystem === "metric") {
    const celsius =
      sourceUnit === "F"
        ? Math.round(((value - 32) * 5) / 9)
        : Math.round(value)
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "celsius",
      unitDisplay: "short",
    }).format(celsius)
  }

  const fahrenheit =
    sourceUnit === "C" ? Math.round((value * 9) / 5 + 32) : Math.round(value)
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "fahrenheit",
    unitDisplay: "short",
  }).format(fahrenheit)
}

// Wind speed arrives as a backend-formatted mph string (e.g. "10 mph" — see
// WeatherService, which hardcodes " mph"). Parse the number out, convert to the
// user's unit system, and re-emit the localized value + label via Intl unit
// style — mirroring formatWeatherTemp.
const MPH_PER_KMH = 1.60934

export const formatWindSpeed = (
  locale: string,
  windSpeed: string | undefined,
  unitSystem: UnitSystem
) => {
  if (!windSpeed) return windSpeed

  const match = windSpeed.trim().match(/^(-?\d+(?:\.\d+)?)\s*(?:mph)?$/i)
  if (!match) return windSpeed

  const mph = Number(match[1])
  if (!Number.isFinite(mph)) return windSpeed

  if (unitSystem === "metric") {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "kilometer-per-hour",
      unitDisplay: "short",
      maximumFractionDigits: 0,
    }).format(mph * MPH_PER_KMH)
  }

  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "mile-per-hour",
    unitDisplay: "short",
    maximumFractionDigits: 0,
  }).format(mph)
}
